// U4 wording and checks for the card's draft: the one line a card that needs a reply shows while it
// has no draft (Claude writing it, paused to save spend, or press `e`), and the placeholder gate
// that keeps a send from arming while a `[[fill: …]]` is left. Pure, so it is tested without a DOM.
import type { CardView, DraftRequestStatus } from "@shared/api";
import type { CardAction, CardBlock, WorkStatus } from "@shared/core";
import { draftCoverage, PAUSED_FOR_SPEND } from "@shared/drafts";
import { hasPlaceholder } from "@shared/placeholders";

export interface DraftLine {
  /** `writing`: Claude is on it; `waiting`: nothing is coming until the owner asks. */
  tone: "writing" | "waiting";
  text: string;
  /** Offer "Press e to draft" after the text. */
  offerDraft: boolean;
}

const WRITING: DraftLine = { tone: "writing", text: "Writing a draft…", offerDraft: false };
const PAUSED: DraftLine = { tone: "waiting", text: PAUSED_FOR_SPEND, offerDraft: true };
const FINISHED = new Set<WorkStatus>(["completed", "failed", "cancelled", "stale"]);

/**
 * What a card that needs a reply says while it has neither a draft nor a question; null when it
 * needs no line (a draft or a question is there, it needs no reply, or it is not waiting for you).
 */
export function draftLine(
  card: Pick<CardView, "kind" | "ask" | "blocks" | "drafting">,
  requested: DraftRequestStatus | null,
  actionable: boolean,
): DraftLine | null {
  if (!actionable || draftCoverage(card) !== "none") return null;
  const work = card.drafting;
  if (work?.status === "queued" || work?.status === "working") return WRITING;
  if (requested === "paused" || work?.result === PAUSED_FOR_SPEND) return PAUSED;
  // The ask's "queued" answer outlives the work it started: once that work ended, it speaks.
  const ended = !!work && FINISHED.has(work.status);
  if (!ended && (requested === "queued" || requested === "working")) return WRITING;
  if (work?.status === "failed")
    return { tone: "waiting", text: "Couldn't write a draft just now.", offerDraft: true };
  return { tone: "waiting", text: "No draft yet.", offerDraft: true };
}

/** The draft text a send would carry, when it still has a placeholder to fill in. */
export function unfilledDraft(
  action: CardAction,
  blocks: readonly CardBlock[],
  values: Record<string, string>,
): { blockId: string; text: string } | null {
  if (action.behavior !== "approve_action" || !action.artifactBlockId) return null;
  const block = blocks.find((b) => b.id === action.artifactBlockId);
  if (block?.type !== "editable_text") return null;
  const text = values[block.id] ?? block.value ?? "";
  return hasPlaceholder(text) ? { blockId: block.id, text } : null;
}
